import {
  FieldAddonDropdown,
  FieldDatePicker,
  FieldDateRangePicker,
  FieldSelect,
  FieldSlider,
  FieldText,
  FlexBox,
  FormField,
  FormGrid,
  IconButton,
} from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { ReactNode, useCallback, useEffect } from 'react';
import { Field, GenericField } from 'redux-form';

import { customFieldsApi } from 'apis/customFields';
import {
  getSystemVariableString,
  SystemVariableId,
  SystemVariableType,
} from 'components/powerFlowShared/helpers/flowVariableHelpers';
import { FlowType } from 'fable/flowEditor';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';

import type { FiltersFormRule } from '../../nodeTypes';
import { MatchingOperator } from '../../types';

import { CustomFieldFormAdapter } from './components/customFieldFormAdapter/customFieldFormAdapter';
import styles from './criteriaFields.module.scss';
import {
  getInputPlaceholder,
  getValidOperator,
  getVariableItems,
  ScopeVariables,
} from './util/helper';

const FieldCustom = Field as unknown as new () => GenericField<any>;

export enum MatchIdType {
  Date = 'Date',
  DateRange = 'Date range',
  Input = 'Input',
  ScopeVariable = 'Variable',
  Values = 'Values',
  SpamProbabilityVariable = 'Spam Threshold',
}

const dateVariable = { id: 'date', name: MatchIdType.Date };
const dateRangeVariable = { id: 'dateRange', name: MatchIdType.DateRange };
const inputVariable = { id: 'input', name: MatchIdType.Input };
const scopeVariable = { id: 'scope', name: MatchIdType.ScopeVariable };
const spamProbabilityVariable = {
  id: 'spam_probability',
  name: MatchIdType.SpamProbabilityVariable,
};
const valuesVariable = { id: 'values', name: MatchIdType.Values };

const systemDateVariable = getSystemVariableString(SystemVariableId.Date);
const systemAuthLevelVariable = getSystemVariableString(
  SystemVariableId.AuthLevel,
);

const systemSpamVariable = getSystemVariableString(
  SystemVariableId.SpamProbability,
);
const systemContactCustomAttributeVariable = getSystemVariableString(
  SystemVariableId.ContactCustomAttributePrefix,
);

type Variable =
  | typeof dateVariable
  | typeof inputVariable
  | typeof scopeVariable
  | typeof spamProbabilityVariable
  | typeof valuesVariable;

const getVariableTypeItems = (
  variable: string,
  flowType: FlowType,
): { id: string; name: string }[] => {
  const isDateVariableSelected = variable === systemDateVariable;
  const isSpamVariableSelected = variable === systemSpamVariable;
  const isMessengerFlow = flowType === FlowType.FLOW_TYPE_MESSENGER;
  const isAuthLevelVariableSelected = variable === systemAuthLevelVariable;
  const isScopeVariableSelected = variable?.startsWith(
    `${SystemVariableType.Scope}:`,
  );

  const isDateMatchShown =
    !isMessengerFlow && (isDateVariableSelected || isScopeVariableSelected);

  if (isSpamVariableSelected) return [spamProbabilityVariable];

  return [
    ...(!isDateVariableSelected && !isAuthLevelVariableSelected
      ? [inputVariable, scopeVariable]
      : []),
    ...(isDateMatchShown ? [dateVariable, dateRangeVariable] : []),
    ...(isAuthLevelVariableSelected ? [valuesVariable, scopeVariable] : []),
  ];
};

const getSelectedValue = (
  items: ReturnType<typeof getVariableTypeItems>,
  variableType?: string,
): string => {
  const selectedItem = items.find((item) => item.name === variableType);

  if (!selectedItem || !variableType) {
    return items[0].name;
  }

  return variableType;
};

const getPrefix = (
  items: ReturnType<typeof getVariableTypeItems>,
  selectedValue: string,
  field: string,
  changeFormField: (variableType: string, field: string) => void,
) => {
  if (items.length === 1) return items[0].name;

  return {
    type: 'custom',
    noWrap: true,
    value: (
      // call stopPropagation when used inside InputSelectionList to prevent
      // InputSelectionList dropdown to open instead of prefix
      <div onClick={(e) => e.stopPropagation()}>
        <FieldAddonDropdown
          items={items}
          meta={{ selectedValue }}
          fixed
          onSelect={(item) => {
            changeFormField('', `${field}.match`);
            changeFormField(item.name, `${field}.variableType`);
            changeFormField('', `${field}.match_custom_attribute_id`);
          }}
        />
      </div>
    ),
  };
};

export function CriteriaFields({
  flowType,
  changeFormField,
  field,
  index,
  onRemove,
  rules,
  totalRows,
  scopeVariables,
}: {
  flowType: FlowType;
  changeFormField: (variableType: string | number, field: string) => void;
  field: string;
  index: number;
  onRemove: (index: number) => void;
  rules: FiltersFormRule[];
  totalRows: number;
  scopeVariables: ScopeVariables;
}): JSX.Element {
  const currentRule: FiltersFormRule = rules && rules[index];
  const featureFlag = useFeatureFlag(['spam-filtering']);
  const isCustomVariable = currentRule.variable?.includes(
    SystemVariableId.ContactCustomAttributePrefix,
  );

  const isCustomMatch =
    typeof currentRule.match === 'string' &&
    currentRule.match.includes(SystemVariableId.ContactCustomAttributePrefix);

  const [readAttributesQuery, { attributes, isSuccess, isUninitialized }] =
    customFieldsApi.useLazyReadAttributesQuery({
      selectFromResult: ({ data, ...rest }) => {
        return {
          ...rest,
          attributes:
            data?.data.attributes.map((a) => ({
              ...a,
              name: a.label,
            })) ?? [],
        };
      },
    });

  const variableItems = getVariableItems({
    flowType,
    scopeVariables,
    selectedVariable: currentRule.variable,
    featureFlags: {
      hasSpamFiltering: featureFlag.spamFiltering,
    },
  });

  const {
    matching_rule: currentMatchingRule,
    variable: currentVariable,
    variableType: currentVariableType,
  } = currentRule;
  const showMatchField = ![
    MatchingOperator.IS_EMPTY,
    MatchingOperator.IS_NOT_EMPTY,
  ].includes(currentMatchingRule);

  // Fetches the custom attributes the first time it is selected in dropdown.
  useEffect(() => {
    if ((isCustomVariable || isCustomMatch) && isUninitialized) {
      readAttributesQuery({ entityId: 'contact' }, true);
    }
  }, [isCustomVariable, isCustomMatch, isUninitialized, readAttributesQuery]);

  const onAttributeChange = useCallback(
    (value) => changeFormField(value, `${field}.match`),
    [changeFormField, field],
  );

  const attribute = isCustomVariable
    ? attributes.find(({ id }) => id === currentRule.custom_attribute_id)
    : undefined;

  function renderMatchField(type?: Variable['name']) {
    const items = getVariableTypeItems(currentVariable as string, flowType);
    const selectedValue = getSelectedValue(items, currentVariableType);

    const matchFieldCommonProps = {
      name: `${field}.match`,
      noLabel: true,
      prefix: getPrefix(items, selectedValue, field, changeFormField),
      required: true,
    };

    switch (type) {
      case MatchIdType.SpamProbabilityVariable:
        return (
          <FieldCustom
            {...matchFieldCommonProps}
            component={FieldSlider}
            min={0}
            max={100}
            formatValue={(value: number): ReactNode => `${value}%`}
          />
        );
      case MatchIdType.Values:
        // Auth level yet the only variable that has a fixed set of values.
        return (
          <FieldCustom
            {...matchFieldCommonProps}
            component={FieldSelect}
            dropdownProps={{ fixed: true }}
            items={[
              { id: 'anonymous', name: 'Anonymous' },
              { id: 'claimed', name: 'Claimed' },
              { id: 'verified', name: 'Verified' },
            ]}
            placeholder="Please select a value"
            searchable
          />
        );

      case MatchIdType.ScopeVariable:
        return (
          <FieldCustom
            {...matchFieldCommonProps}
            component={FieldSelect}
            dropdownProps={{ fixed: true }}
            items={variableItems}
            placeholder="Please select a variable"
            searchable
          />
        );
      case MatchIdType.Date:
        return (
          <FieldCustom
            {...matchFieldCommonProps}
            component={FieldDatePicker}
            dropdownProps={{ fixed: true }}
            datePickerProps={{ withTimezoneSelector: true }}
            placeholder="Select a date"
          />
        );
      case MatchIdType.DateRange:
        return (
          <FieldCustom
            {...matchFieldCommonProps}
            component={FieldDateRangePicker}
            dropdownProps={{ fixed: true }}
            dateRangePickerProps={{ withTimezoneSelector: true }}
            placeholder="Select a date"
          />
        );
      default:
        // Fallback to regular input if attribute was not found (attribute was removed or invalid uuid).
        if (attribute) {
          return (
            <FieldCustom
              attribute={attribute}
              name={`${field}.match`}
              component={CustomFieldFormAdapter}
              prefix={matchFieldCommonProps.prefix}
              onAttributeChange={onAttributeChange}
            />
          );
        }

        return (
          <FieldCustom
            {...matchFieldCommonProps}
            component={FieldText}
            placeholder={getInputPlaceholder(
              currentVariable,
              currentMatchingRule,
            )}
          />
        );
    }
  }

  const rootClass = classnames({
    [styles.root]: true,
    [styles.rootFirst]: index === 0,
  });

  return (
    <FlexBox className={rootClass}>
      <FormGrid>
        <FormField span={3}>
          <FieldCustom
            component={FieldSelect}
            dropdownProps={{
              fixed: true,
            }}
            items={variableItems}
            name={`${field}.variable`}
            noAlert
            noLabel
            placeholder="Select variable"
            required
            onChange={(_event, newValue, previousValue) => {
              const items = getVariableTypeItems(newValue, flowType);
              const selectedValue = getSelectedValue(
                items,
                currentVariableType,
              );

              if (
                (newValue !== systemDateVariable &&
                  previousValue === systemDateVariable) ||
                (newValue !== systemSpamVariable &&
                  previousValue === systemSpamVariable)
              ) {
                changeFormField('', `${field}.match`);
                changeFormField(selectedValue, `${field}.variableType`);
              }

              if (
                previousValue === systemContactCustomAttributeVariable &&
                newValue !== systemContactCustomAttributeVariable
              ) {
                changeFormField('', `${field}.custom_attribute_id`);
              }

              if (
                newValue === systemDateVariable &&
                previousValue !== systemDateVariable
              ) {
                changeFormField('', `${field}.match`);
                changeFormField(MatchIdType.Date, `${field}.variableType`);
              }

              if (
                newValue === systemAuthLevelVariable &&
                previousValue !== systemAuthLevelVariable
              ) {
                changeFormField('', `${field}.match`);
                changeFormField(MatchIdType.Values, `${field}.variableType`);
              }

              if (
                newValue !== systemAuthLevelVariable &&
                previousValue === systemAuthLevelVariable
              ) {
                changeFormField('', `${field}.match`);
                changeFormField(MatchIdType.Input, `${field}.variableType`);
              }

              if (
                newValue === systemSpamVariable &&
                previousValue !== systemSpamVariable
              ) {
                changeFormField(50, `${field}.match`);
                changeFormField(
                  MatchIdType.SpamProbabilityVariable,
                  `${field}.variableType`,
                );
              }

              // When the variable changes, we have to align the operator,
              // and use the first one from the list. In this case we can
              // prevent the operator select from being empty.
              const [firstOperator] = getValidOperator(newValue);
              changeFormField(firstOperator.id, `${field}.matching_rule`);
            }}
          />
        </FormField>
        <FormField span={3}>
          <Field
            required
            component={FieldSelect}
            name={`${field}.matching_rule`}
            items={getValidOperator(currentRule.variable, {
              matchingRule: currentRule.matching_rule,
              variableType: attribute?.type === 'select' ? 'set' : undefined,
            })}
            dropdownProps={{ fixed: true }}
            noAlert
            noLabel
          />
        </FormField>

        {isCustomVariable && isSuccess && (
          <FormField>
            <Field
              required
              component={FieldSelect}
              name={`${field}.custom_attribute_id`}
              placeholder="Select attribute"
              items={attributes}
              dropdownProps={{
                fixed: true,
                className: styles.customVariableDropdown,
              }}
              noLabel
            />
          </FormField>
        )}

        {showMatchField && (
          <>
            <FormField>{renderMatchField(currentVariableType)}</FormField>
            {isCustomMatch && isSuccess && (
              <FormField>
                <Field
                  required
                  component={FieldSelect}
                  name={`${field}.match_custom_attribute_id`}
                  placeholder="Select attribute"
                  items={attributes}
                  dropdownProps={{
                    fixed: true,
                    className: styles.customVariableDropdown,
                  }}
                  noLabel
                />
              </FormField>
            )}
          </>
        )}
      </FormGrid>
      <FlexBox alignItems="center" className={styles.trashButton}>
        <IconButton
          icon="trash"
          variant="tertiary"
          type="button"
          disabled={totalRows === 1}
          onClick={() => onRemove(index)}
        />
      </FlexBox>
    </FlexBox>
  );
}
